"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MarkersEditor = void 0;
var React = __importStar(require("react"));
var toMarkerAreaState_1 = require("./toMarkerAreaState");
var MarkupImage_1 = require("./MarkupImage");
var useEditedImage_1 = require("./useEditedImage");
var usePaddedImageDetails_1 = require("./usePaddedImageDetails");
var toCropAreaState_1 = require("./toCropAreaState");
var markerjs2_1 = require("markerjs2");
var MediaContext_1 = require("../../MediaContext");
var lodash_1 = require("lodash");
var initMarkerArea_1 = require("./initMarkerArea");
// Enable marker.js license. Removes the logo from the editor.
// This key is already exposed since we have to set this on the frontend. It's low risk so we
// hardcode it for simplicity.
markerjs2_1.Activator.addKey('MJS2-F803-M284-3658');
function MarkersEditor(_a) {
    var imageInfo = _a.imageInfo, sourceImage = _a.sourceImage, onClose = _a.onClose, onRender = _a.onRender;
    var mediaContext = (0, MediaContext_1.useMediaContext)();
    var _b = (0, usePaddedImageDetails_1.usePaddedImageDetails)(imageInfo, sourceImage), shouldUsePaddedImage = _b.shouldUsePaddedImage, paddedImageInfo = _b.paddedImageInfo, paddedImageSrc = _b.paddedImageSrc, paddedCropAreaState = _b.paddedCropAreaState;
    var _c = React.useState(null), paddedImage = _c[0], setPaddedImage = _c[1];
    var imageElementToEdit = shouldUsePaddedImage ? paddedImage !== null && paddedImage !== void 0 ? paddedImage : sourceImage : sourceImage;
    var _d = React.useState(null), editedImageElement = _d[0], setEditedImageElement = _d[1];
    var memoizedOnClose = React.useCallback(onClose, []);
    var memoizedOnRender = React.useCallback(onRender, []);
    var cropAreaState = shouldUsePaddedImage ? paddedCropAreaState : (0, toCropAreaState_1.toCropAreaState)(imageInfo);
    // If padding isn't required and there's no previous edit state saved, we can just use the
    // original source image
    var imageElement = !shouldUsePaddedImage && !cropAreaState ? sourceImage : editedImageElement;
    var editedImageSrc = (0, useEditedImage_1.useEditedImage)(imageElementToEdit, cropAreaState);
    var alreadyClosingDueToCleanup = React.useRef(false);
    React.useEffect(function () {
        alreadyClosingDueToCleanup.current = false;
        if (!imageElement) {
            return;
        }
        var _a = (0, initMarkerArea_1.initMarkerArea)(imageElement, imageInfo, mediaContext), markerArea = _a.markerArea, cleanupMarkerArea = _a.cleanupMarkerArea, defaultStrokeWidth = _a.defaultStrokeWidth;
        markerArea.addEventListener('close', function () {
            // Normally, this runs first, the cleanup.
            // However, if cleanup runs outside of component unmount (such as in React Strict Mode),
            //  a "cleanup" close can trigger this, which results in the markup editor *actually* closing,
            //  usually right after it opens.
            if (!alreadyClosingDueToCleanup.current) {
                memoizedOnClose();
            }
        });
        var markersStateChanged = false;
        markerArea.addEventListener('markercreate', function () { return (markersStateChanged = true); });
        markerArea.addEventListener('markerdelete', function () { return (markersStateChanged = true); });
        markerArea.addEventListener('markerchange', function () { return (markersStateChanged = true); });
        markerArea.addEventListener('render', function (event) {
            // There are bugs in the source code of markerjs2 where some events are missed.
            // Catching these require an extra check.
            // First, check if there have been any events caught
            if (!markersStateChanged) {
                // But if not, double check by stringifying and comparing initial and current states.
                if (JSON.stringify(event.state) === JSON.stringify(markerAreaState)) {
                    memoizedOnClose();
                    return;
                }
            }
            var data = __assign(__assign({}, event.state), { markers: event.state.markers.map(function (marker) {
                    // Save ratio of chosen stroke width compared to default so we can convert the stroke
                    // width when the editor is opened on a different size screen.
                    if (marker['strokeWidth'] !== undefined) {
                        marker['strokeWidthRatioToDefault'] = (0, lodash_1.round)(marker['strokeWidth'] / defaultStrokeWidth, 1);
                    }
                    return marker;
                }) });
            memoizedOnRender(event.dataUrl, {
                type: 'MARKER_JS',
                version: '2',
                data: data,
            });
        });
        markerArea.show();
        configureCustomFontForMarkerArea(markerArea);
        var markerAreaState = (0, toMarkerAreaState_1.toMarkerAreaState)(imageInfo, defaultStrokeWidth);
        if (markerAreaState) {
            markerArea.restoreState(markerAreaState);
        }
        return function () {
            alreadyClosingDueToCleanup.current = true;
            markerArea.close();
            cleanupMarkerArea();
        };
    }, [imageElement, imageInfo, memoizedOnClose, memoizedOnRender, mediaContext]);
    return (React.createElement(React.Fragment, null,
        React.createElement(MarkupImage_1.MarkupImage, { imageInfo: paddedImageInfo, imageSrc: paddedImageSrc, onLoad: function (image) { return setPaddedImage(image); } }),
        React.createElement(MarkupImage_1.MarkupImage, { imageInfo: imageInfo, imageSrc: editedImageSrc, onLoad: function (image) { return setEditedImageElement(image); } })));
}
exports.MarkersEditor = MarkersEditor;
/**
 * This should be called after markerArea.show()
 *
 * Add a style tag to the SVG to ensure our default font is used in the text markers.
 * For more info, see markerjs2's docs on adding custom fonts: https://markerjs.com/docs/custom-fonts/
 */
function configureCustomFontForMarkerArea(markerArea) {
    var style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    style.setAttribute('type', 'text/css');
    style.innerHTML = "text{font-family: ".concat(markerArea.settings.defaultFontFamily, ";}");
    markerArea.addDefs(style);
}
